// Convert start_date and end_date fields from Storyblok format (YYYY-MM-DD 00:00)
// to explicit Central Timezone datetime strings.
const STORYBLOK_DATETIME = /^(\d{4}-\d{2}-\d{2}) 00:00$/;

const DAY_BOUNDARIES: Record<string, string> = {
    start_date: '00:00:00',
    end_date: '23:59:59',
};

export function normalizeStoryblokDates<T>(node: T): T {
    if (Array.isArray(node)) {
        node.forEach((item) => normalizeStoryblokDates(item));
        return node;
    }

    if (node === null || typeof node !== 'object') {
        return node;
    }

    const record = node as Record<string, unknown>;

    for (const [key, value] of Object.entries(record)) {
        const boundary = DAY_BOUNDARIES[key];
        if (boundary && typeof value === 'string') {
            const parsedDate = value.match(STORYBLOK_DATETIME)?.[1];
            if (parsedDate) {
                // Get offset by providing a "guess", which is the datetime parsed as UTC.
                // Always correct because the offset from parsing as UTC never crosses DST boundaries at 2 AM.
                const offset = getCentralTimezoneOffset(
                    new Date(`${parsedDate}T${boundary}Z`),
                );
                record[key] = `${parsedDate}T${boundary}${offset}`;
            }
        } else {
            normalizeStoryblokDates(value);
        }
    }

    return node;
}

const centralTimezoneFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    timeZoneName: 'longOffset',
});

// Returns the ISO 8601 offset of Central Time at the given instant,
// e.g. "-05:00" (CDT) or "-06:00" (CST).
export function getCentralTimezoneOffset(instant: Date): string {
    const offset = centralTimezoneFormatter
        .formatToParts(instant)
        .find((part) => part.type === 'timeZoneName')!.value;

    return offset.replace('GMT', '');
}
